import React, { useState, useEffect, useCallback } from 'react'
import { gazoKensakuService } from '../services/gazoKensakuService'
import { useBlobUrlFromApi } from '../hooks/useBlobUrl'

const ImageCard = ({
  image,
  className = '',
  dragListeners = {},
  // Service configuration
  imageService = null,
  serviceParams = [],
  // Page type to control what information is displayed
  pageType = 'gazoKensaku' // 'gazoKensaku', 'satsueiDataKensaku', 'satsueiDataToroku', 'share'
}) => {
  const [showTags, setShowTags] = useState(false)
  const [imageTags, setImageTags] = useState('')
  const [isLoadingTags, setIsLoadingTags] = useState(false)

  // Create stable dependencies to prevent unnecessary re-fetches
  const imageKey = `${image.objectId}-${image.objectImageId}-${image.objectImageEditId || image.maxObjectImageEditId || 'null'}-${image.filenameExtension}-${image.isUploaded || false}`
  
  // Early check to prevent hook from running if we know the image shouldn't be loaded
  const shouldSkipImageLoad = image.isUploaded === 0 || image.isUploaded === "0" || image.isUploaded === false
  
  // Create stable fetch function with useCallback to prevent infinite re-renders
  const fetchImage = useCallback(async () => {
    try {
      // Early return if we should skip image loading (double check)
      if (shouldSkipImageLoad) {
        return null
      }

      if (image.objectId === null || image.objectId === undefined ||
          image.objectImageId === null || image.objectImageId === undefined ||
          !image.filenameExtension) {
        return null
      }

      // For SatsueiDataKensaku, check if we have the necessary image properties
      // If filenameExtension is missing or empty, the image likely doesn't exist
      if (!image.filenameExtension || image.filenameExtension.trim() === '') {
        return null
      }

      // For SatsueiDataKensaku, check if the search result indicates no image
      // This is a heuristic based on common patterns in search results
      if (pageType === 'satsueiDataKensaku' && 
          image.objectImageId === 0 && 
          (!image.filenameExtension || image.filenameExtension.trim() === '')) {
        return null
      }

      // Additional heuristic: if filenameExtension is a common "no image" indicator
      if (pageType === 'satsueiDataKensaku' && 
          image.filenameExtension && 
          (image.filenameExtension.toLowerCase() === 'none' || 
           image.filenameExtension.toLowerCase() === 'null' || 
           image.filenameExtension.toLowerCase() === 'empty')) {
        return null
      }

      const objectImageEditId = image.objectImageEditId ?? image.maxObjectImageEditId ?? null

      // Add timeout to prevent long loading for non-existent images
      // Use shorter timeout for SatsueiDataKensaku as it has more non-existent images
      const timeoutDuration = pageType === 'satsueiDataKensaku' ? 3000 : 5000
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Image load timeout')), timeoutDuration)
      })

      const fetchPromise = (async () => {
        // Use the provided service or fallback to default behavior
        if (imageService && typeof imageService.getImage === 'function') {
          // Use custom service with custom parameters
          return await imageService.getImage(
            image.objectId,
            image.objectImageId,
            objectImageEditId,
            image.filenameExtension,
            image.isUploaded,
            ...serviceParams
          )
        } else {
          // Fallback: use gazoKensakuService
          return await gazoKensakuService.getImage(
            image.objectId,
            image.objectImageId,
            objectImageEditId,
            image.filenameExtension,
            image.isUploaded
          )
        }
      })()

      // Race between fetch and timeout
      const result = await Promise.race([fetchPromise, timeoutPromise])
      
      // Additional validation for SatsueiDataKensaku
      if (pageType === 'satsueiDataKensaku' && result) {
        // Check if the blob is actually valid (not empty or error response)
        if (result.size === 0) {
          return null
        }
        
        // Check if the blob is too small to be a valid image (likely an error response)
        if (result.size < 100) {
          return null
        }
      }
      
      return result
    } catch (error) {
      console.error('Error in ImageCard fetch function:', error)
      // Return null instead of throwing to show "no image" state
      return null
    }
  }, [imageKey, imageService, serviceParams, shouldSkipImageLoad, pageType, image])

  // Use the blob URL hook for automatic cleanup - centralized blob management
  // Only call the hook if we should actually try to load the image
  const { blobUrl: imageUrl, isLoading, error } = useBlobUrlFromApi(
    shouldSkipImageLoad ? null : fetchImage,
    // Stable dependencies - only change when image data actually changes
    [imageKey, imageService, serviceParams, shouldSkipImageLoad],
    // MIME type
    'image/jpeg'
  )

  // Load image tags separately using getObjectImageTag API (only for gazoKensaku)
  useEffect(() => {
    const loadImageTags = async () => {
      // Only load tags for gazoKensaku
      if (pageType !== 'gazoKensaku') {
        setImageTags('')
        return
      }

      if (image.objectId !== null && image.objectId !== undefined && 
          image.objectImageId !== null && image.objectImageId !== undefined) {
        setIsLoadingTags(true)
        try {
          const response = await gazoKensakuService.getObjectImageTag(image.objectId, image.objectImageId)
          const tags = response.imageTagList?.map(tag => tag.imageTag).join(' ') || ''
          setImageTags(tags)
        } catch (error) {
          console.error('Failed to load image tags:', error)
          setImageTags('')
        } finally {
          setIsLoadingTags(false)
        }
      } else {
        setImageTags('')
      }
    }

    loadImageTags()
  }, [image.objectId, image.objectImageId, pageType])

  return (
    <div className={`relative border border-gray-300 bg-gray-800 w-[120px] h-[120px] md:w-[140px] md:h-[140px] lg:w-[150px] lg:h-[150px] cursor-pointer ${className}`}>
      {/* Object ID and order index */}
      <div className="absolute top-0 left-0 z-10 flex flex-col">
        {/* Object ID - show for gazoKensaku and satsueiDataKensaku */}
        {(pageType === 'gazoKensaku' || pageType === 'satsueiDataKensaku') && (
          <div className="max-w-[110px] md:max-w-[130px] lg:max-w-[140px] text-white bg-gray-800 px-1 md:px-2 py-1 border border-white overflow-hidden text-xs">
            {image.objectId || '-'}
          </div>
        )}
        
        {/* Order Index - only show for gazoKensaku */}
        {pageType === 'gazoKensaku' && image.orderIndex !== null && image.orderIndex !== undefined && (
          <div className="bg-yellow-100 border border-white text-xs px-1 md:px-2 py-1 rounded-full m-1 w-fit inline-flex items-center justify-center">
            {(image.orderIndex || 0) + 1}
          </div>
        )}
        
        {/* Image Tags - only show for gazoKensaku */}
        {pageType === 'gazoKensaku' && (imageTags || isLoadingTags) && (
          <button
            onClick={() => setShowTags(!showTags)}
            className="bg-blue-100 border border-white text-xs px-1 md:px-2 py-1 hover:bg-blue-200 transition-colors flex items-center justify-center w-fit"
            disabled={isLoadingTags}
          >
            {isLoadingTags ? (
              <div className="w-3 h-3 border border-gray-600 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              'タグ'
            )}
          </button>
        )}
      </div>

      {/* Image - only this area is draggable */}
      <div
        className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
        {...dragListeners}
      >
        {shouldSkipImageLoad ? (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-400 mx-auto mb-2"></div>
              <div className="text-gray-500 text-xs">転送中...</div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-gray-500 text-xs">読み込み中...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center w-full h-full">
            <img
              src="/core/img/errorimage.png"
              alt="エラー"
              className="max-w-[118px] max-h-[118px] md:max-w-[138px] md:max-h-[138px] lg:max-w-[148px] lg:max-h-[148px] object-contain"
            />
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={`Image ${image.objectId || 'unknown'}`}
            className="max-w-[118px] max-h-[118px] md:max-w-[138px] md:max-h-[138px] lg:max-w-[148px] lg:max-h-[148px] object-contain"
            onError={() => console.error('Image failed to load')}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <img
              src="/core/img/misatsuei.png"
              alt="画像なし"
              className="max-w-[118px] max-h-[118px] md:max-w-[138px] md:max-h-[138px] lg:max-w-[148px] lg:max-h-[148px] object-contain"
            />
          </div>
        )}
      </div>

      {/* Tags popup - only show for gazoKensaku */}
      {showTags && pageType === 'gazoKensaku' && (
        <div className="absolute top-0 left-0 z-20 bg-white border border-gray-400 shadow-lg p-2 w-[110px] md:w-[130px] lg:w-[140px] text-xs rounded">
          {isLoadingTags ? (
            <div className="mb-2 flex items-center justify-center">
              <div className="w-4 h-4 border border-gray-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : imageTags ? (
            <div className="mb-2 break-words">{imageTags}</div>
          ) : (
            <div className="mb-2 text-gray-500">タグなし</div>
          )}
          <button
            onClick={() => setShowTags(false)}
            className="bg-gray-200 border border-gray-400 text-xs px-2 py-1 w-full text-center hover:bg-gray-300 transition-colors rounded"
          >
            閉じる
          </button>
        </div>
      )}
    </div>
  )
}

export default ImageCard
